import { useState } from "react";
import { FolderOpen, Plus } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { useDocumentCategoryLabel } from "@/components/documents/use-document-category-label";
import { SettingsPageHeader, SettingsPanel } from "./SettingsSection";
import { CategoryList } from "./categories/CategoryList";
import { CategoryFormDialog } from "./categories/CategoryFormDialog";
import { CategoryDeleteDialog } from "./categories/CategoryDeleteDialog";
import {
  useDocumentCategories,
  useCreateDocumentCategory,
  useUpdateDocumentCategory,
  useDeleteDocumentCategory,
} from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";

interface Category {
  _id: Id<"documentCategories">;
  name: string;
  slug?: string;
  icon: string;
  isSystem: boolean;
}

export function SettingsCategoriesSection() {
  const { t } = useTranslation(["settings"]);
  const getCategoryLabel = useDocumentCategoryLabel();
  const { categories, isLoading: isCategoriesLoading } =
    useDocumentCategories();
  const createCategory = useCreateDocumentCategory();
  const updateCategory = useUpdateDocumentCategory();
  const deleteCategory = useDeleteDocumentCategory();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(
    null,
  );

  const handleCreate = () => {
    setEditingCategory(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormDialogOpen(true);
  };

  const handleDelete = (category: Category) => {
    setDeletingCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: { name: string; icon: string }) => {
    if (editingCategory) {
      await updateCategory({ id: editingCategory._id, ...data });
    } else {
      await createCategory(data);
    }
    setFormDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (deletingCategory) {
      await deleteCategory({ id: deletingCategory._id });
      setDeleteDialogOpen(false);
    }
  };

  if (isCategoriesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-muted">
          {t("settings:common.loadingCategories")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("settings:sections.organization")}
        title={t("settings:categories.title")}
        description={t("settings:common.categoriesDescription")}
        action={
          <Button variant="primary" icon={Plus} onClick={handleCreate}>
            {t("settings:common.addCategory")}
          </Button>
        }
      />
      <SettingsPanel>
        {categories.length > 0 ? (
          <CategoryList
            categories={categories}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-48 rounded-xl border border-dashed border-border">
            <FolderOpen className="h-8 w-8 text-muted mb-3" />
            <p className="text-sm text-muted">
              {t("settings:common.noCategories")}
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={handleCreate}
            >
              {t("settings:common.createFirstCategory")}
            </Button>
          </div>
        )}

        <CategoryFormDialog
          open={formDialogOpen}
          onClose={() => setFormDialogOpen(false)}
          mode={editingCategory ? "edit" : "create"}
          initialData={editingCategory ?? undefined}
          onSubmit={handleFormSubmit}
        />

        <CategoryDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          categoryName={
            deletingCategory
              ? getCategoryLabel({
                  name: deletingCategory.name,
                  slug: deletingCategory.slug,
                })
              : ""
          }
          categoryId={deletingCategory?._id ?? null}
          onConfirm={handleDeleteConfirm}
        />
      </SettingsPanel>
    </div>
  );
}
